import { pascalCase } from 'change-case';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

export const isMessage = (message) => isObject(message) && typeof message.id !== 'undefined';

export const shuffle = (array) => {
    if (!isArray(array)) {
        return array;
    }
    let currentIndex = array.length;
    let temporaryValue;
    let randomIndex;
    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex]; // eslint-disable-line
        array[randomIndex] = temporaryValue; // eslint-disable-line
    }
    return array;
};

export const getComponentFromName = (components, name, defaultComponentName = null) => {
    const defaultComponent =
        (isString(defaultComponentName)
            ? components[pascalCase(defaultComponentName)] || components[defaultComponentName]
            : defaultComponentName) || null;
    if (name === null) {
        return defaultComponent;
    }
    const componentName = pascalCase(name);
    return components[componentName] || components[name] || defaultComponent;
};

export const getImageSize = (image, size) =>
    image !== null && image !== undefined
        ? (image.sizes || []).find((it) => it.id === size) || image
        : null;

export const getImageSizeUrl = (image, size) => {
    const imageAtSize = getImageSize(image, size);
    return imageAtSize !== null ? imageAtSize.url || null : null;
};

export const getImageAtSize = (image, size, webp = false) => {
    if (image === null) {
        return null;
    }
    const { sizes: imagesSizes = {}, ...originalImage } = image;
    return (
        [...(isArray(size) ? size : [size]), 'original']
            .reduce(
                (acc, currentSize) =>
                    webp ? [...acc, `${currentSize}_webp`, currentSize] : [...acc, currentSize],
                [],
            )
            .reduce(
                (selectedImage, currentSize) => selectedImage || imagesSizes[currentSize] || null,
                null,
            ) || originalImage
    );
};

export const convertUppyToMedia = (it) => {
    const type = it.data.type.split('/')[0];
    const thumbnail = it.transloadit[`${type}_thumbnail`] || null;
    const original = it.transloadit[`${type}_original`] || null;
    return {
        handle: it.id,
        type,
        name: it.meta.name,
        mime: it.data.type,
        size: it.data.size,
        url: original !== null ? original.ssl_url || original.url : null,
        thumbnail_url: thumbnail !== null ? thumbnail.ssl_url || thumbnail.url : null,
        metadata: {
            ...(original !== null ? original.meta || null : null),
            filename: it.meta.filename,
            transloadit: it.transloadit.results || null,
        },
    };
};

export const getUniqueArray = (array, key = 'id') => {
    if (!isArray(array)) {
        return array;
    }
    const uniqueIds = [];
    return array.filter((item) => {
        const valid = uniqueIds.indexOf(item[key]) === -1;
        uniqueIds.push(item.id);
        return valid;
    });
};

export const addNonBreakingSpaces = (str) => str.replace(/&nbsp;/g, '\u00a0');

export const cleanExternalLinks = (str) =>
    str !== null
        ? str.replace(/(<a\\b[^<>]*href=['"]?http[^<>]+)>/gi, '$1 target="_blank">')
        : null;

export const getItemGradientStyle = ({ type, colorGradient = {} }) => {
    const { from = null, to = null } = colorGradient;
    return from !== null && to !== null && type === 'article'
        ? {
              backgroundImage: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
          }
        : null;
};

export const isExternalUrl = (url) => url !== null && url.match(/^https?:\/\//) !== null;

export const capitalizeFirstLetter = (string) =>
    string ? string.charAt(0).toUpperCase() + string.slice(1) : null;

export const getCurrencySymbol = (currency) => {
    const currencySymbolsMap = {
        cad: '$',
        usd: '$',
        eur: '€',
        mxn: '$',
        jpy: '¥', // ¥ not available with current font (FRAC)
    };
    const symbol = currencySymbolsMap[currency.toLowerCase()];
    return typeof symbol !== 'undefined' ? symbol : '$';
};

/* eslint-disable */
export const ease = (type = 'linear', x) => ({
    linear: (x) => x,
    inQuad: (x) => x * x,
    outQuad: (x) => 1 - (1 - x) * (1 - x),
    inOutQuad: (x) => (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2),
    inCubic: (x) => x * x * x,
    outCubic: (x) => 1 - (1 - x) ** 3,
    inOutCubic: (x) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2),
    inQuart: (x) => x * x * x * x,
    outQuart: (x) => 1 - (1 - x) ** 4,
    inOutQuart: (x) => (x < 0.5 ? 8 * x * x * x * x : 1 - (-2 * x + 2) ** 4 / 2),
    inQuint: (x) => x * x * x * x * x,
    outQuint: (x) => 1 - (1 - x) ** 5,
    inOutQuint: (x) => (x < 0.5 ? 16 * x * x * x * x * x : 1 - (-2 * x + 2) ** 5 / 2),
    inSine: (x) => 1 - Math.cos((x * Math.PI) / 2),
    outSine: (x) => Math.sin((x * Math.PI) / 2),
    inOutSine: (x) => -(Math.cos(Math.PI * x) - 1) / 2,
    inExpo: (x) => (x === 0 ? 0 : 2 ** (10 * x - 10)),
    outExpo: (x) => (x === 1 ? 1 : 1 - 2 ** (-10 * x)),
    inOutExpo: (x) =>
        x === 0
            ? 0
            : x === 1
            ? 1
            : x < 0.5
            ? 2 ** (20 * x - 10) / 2
            : (2 - 2 ** (-20 * x + 10)) / 2,
    inCirc: (x) => 1 - Math.sqrt(1 - x ** 2),
    outCirc: (x) => Math.sqrt(1 - (x - 1) ** 2),
    inOutCirc: (x) =>
        x < 0.5
            ? (1 - Math.sqrt(1 - (2 * x) ** 2)) / 2
            : (Math.sqrt(1 - (-2 * x + 2) ** 2) + 1) / 2,
    inBack: (x) => c3 * x * x * x - c1 * x * x,
    outBack: (x) => 1 + c3 * (x - 1) ** 3 + c1 * (x - 1) ** 2,
    inOutBack: (x) =>
        x < 0.5
            ? ((2 * x) ** 2 * ((c2 + 1) * 2 * x - c2)) / 2
            : ((2 * x - 2) ** 2 * ((c2 + 1) * (x * 2 - 2) + c2) + 2) / 2,
    inElastic: (x) =>
        x === 0 ? 0 : x === 1 ? 1 : -(2 ** (10 * x - 10)) * Math.sin((x * 10 - 10.75) * c4),
    outElastic: (x) =>
        x === 0 ? 0 : x === 1 ? 1 : 2 ** (-10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1,
    inOutElastic: (x) =>
        x === 0
            ? 0
            : x === 1
            ? 1
            : x < 0.5
            ? -(2 ** (20 * x - 10) * Math.sin((20 * x - 11.125) * c5)) / 2
            : (2 ** (-20 * x + 10) * Math.sin((20 * x - 11.125) * c5)) / 2 + 1,
});
/* eslint-enable */

export const storageAvailable = (type) => {
    const storage = window[type] ? window[type] : null;
    if (!storage) return false;

    try {
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return (
            e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === 'QuotaExceededError' ||
                // Firefox
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage.length !== 0
        );
    }
};
